import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Check, Copy, ExternalLink, EyeOff, MessageSquare, PenSquare, Pin, Play, RefreshCw, Repeat2, RotateCcw, Share2, Sparkles, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getPlatformDisplayName } from '@/lib/utils';
import { PinGate, isUnlockedThisSession } from '@/components/dashboard/PinGate';
import { useLogSurfaceVisit } from '@/hooks/useActivityLog';
import { ComposeDialog } from '@/components/dashboard/ComposeDialog';

const FEED_LIMIT = 100;
const STALE_MS = 60_000;

interface FeedItem {
  id: string;
  platform: string;
  author: string | null;
  title: string | null;
  body: string | null;
  url: string | null;
  published_at: string | null;
  fetched_at: string;
  rule_tags: string[];
  is_pinned: boolean;
  is_demoted: boolean;
  is_filtered: boolean;
  is_pending_review: boolean;
  toxicity_score: number | null;
  source_name: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content_json: any;
}

interface FollowingFeedItem {
  id: string;
  curator_id: string;
  platform: string;
  author: string | null;
  title: string | null;
  body: string | null;
  url: string | null;
  published_at: string | null;
  fetched_at: string;
  source_name: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content_json: any;
  curator_username: string | null;
  curator_full_name: string | null;
  curator_avatar_url: string | null;
}

// ── Media extraction ────────────────────────────────────────────────────────

interface ItemMedia {
  images: string[];   // displayable image URLs
  videoId?: string;   // YouTube video ID for embed
}

function getMediaFromItem(item: FeedItem): ItemMedia {
  const cj = item.content_json;
  if (!cj) return { images: [] };

  // RSS — image extracted by the backend fetcher from enclosure / media tags / img HTML
  if (item.platform === 'rss') {
    const img = cj.image_url;
    return { images: img ? [img] : [] };
  }

  // YouTube — thumbnail + video ID
  if (item.platform === 'youtube') {
    const thumbs = cj.snippet?.thumbnails;
    const thumb = thumbs?.maxres?.url || thumbs?.high?.url || thumbs?.medium?.url || null;
    const videoId = cj.contentDetails?.videoId || cj.snippet?.resourceId?.videoId;
    return { images: thumb ? [thumb] : [], videoId };
  }

  // Bluesky — embedded images or link-card thumb
  if (item.platform === 'bluesky') {
    const embed = cj.embed;
    if (!embed) return { images: [] };
    const type: string = embed.$type ?? '';
    if (type === 'app.bsky.embed.images#view') {
      const imgs = (embed.images ?? [])
        .map((img: { thumb?: string; fullsize?: string }) => img.thumb || img.fullsize)
        .filter(Boolean);
      return { images: imgs };
    }
    if (type === 'app.bsky.embed.external#view') {
      const thumb = embed.external?.thumb;
      return { images: thumb ? [thumb] : [] };
    }
    if (type === 'app.bsky.embed.video#view') {
      return { images: embed.thumbnail ? [embed.thumbnail] : [] };
    }
    return { images: [] };
  }

  // Reddit — gallery, preview, or thumbnail
  if (item.platform === 'reddit') {
    if (cj.media_metadata) {
      const imgs = Object.values(cj.media_metadata as Record<string, { status: string; e: string; s?: { u: string }; p?: { u: string }[] }>)
        .filter((m) => m.status === 'valid' && m.e === 'Image')
        .map((m) => {
          const src = m.s?.u || m.p?.[m.p.length - 1]?.u;
          return src ? (src as string).replace(/&amp;/g, '&') : null;
        })
        .filter((s): s is string => Boolean(s));
      if (imgs.length) return { images: imgs };
    }
    const previewSrc = cj.preview?.images?.[0]?.source?.url;
    if (previewSrc) return { images: [(previewSrc as string).replace(/&amp;/g, '&')] };
    const thumb = cj.thumbnail as string | undefined;
    if (thumb && !['self', 'default', 'nsfw', 'spoiler', 'image', ''].includes(thumb) && thumb.startsWith('http')) {
      return { images: [thumb] };
    }
    return { images: [] };
  }

  return { images: [] };
}

// ── Per-platform engagement actions ────────────────────────────────────────
// inApp actions stay inside SocialScoop. Only 'open-original' opens a new tab.

interface EngageAction {
  label: string;
  icon: React.ReactNode;
  inApp?: 'reply' | 'repost' | 'share';  // handled in-app
  href?: string;                           // only for 'open-original'
}

function getEngagementActions(item: FeedItem): EngageAction[] {
  const openOriginal: EngageAction = {
    label: 'Open original',
    icon: <ExternalLink className="h-3.5 w-3.5" />,
    href: item.url ?? undefined,
  };

  if (item.platform === 'reddit') {
    return [
      { label: 'Comment', icon: <MessageSquare className="h-3.5 w-3.5" />, inApp: 'reply' },
      { label: 'Share link', icon: <Share2 className="h-3.5 w-3.5" />, inApp: 'share' },
      openOriginal,
    ];
  }
  if (item.platform === 'bluesky') {
    return [
      { label: 'Reply', icon: <MessageSquare className="h-3.5 w-3.5" />, inApp: 'reply' },
      { label: 'Repost', icon: <Repeat2 className="h-3.5 w-3.5" />, inApp: 'repost' },
      { label: 'Share link', icon: <Share2 className="h-3.5 w-3.5" />, inApp: 'share' },
      openOriginal,
    ];
  }
  if (item.platform === 'twitter') {
    return [
      { label: 'Reply', icon: <MessageSquare className="h-3.5 w-3.5" />, inApp: 'reply' },
      { label: 'Repost', icon: <Repeat2 className="h-3.5 w-3.5" />, inApp: 'repost' },
      { label: 'Share link', icon: <Share2 className="h-3.5 w-3.5" />, inApp: 'share' },
      openOriginal,
    ];
  }
  if (!item.url) return [];
  // YouTube, RSS, etc. — share link + open original
  return [
    { label: 'Share link', icon: <Share2 className="h-3.5 w-3.5" />, inApp: 'share' },
    openOriginal,
  ];
}

function formatAuthor(platform: string, author: string | null): string {
  if (!author) return '';
  if (platform === 'reddit') return `u/${author}`;
  if (platform === 'bluesky') return `@${author}`;
  return author;
}

function platformLabel(item: FeedItem): string {
  if (item.platform === 'rss') return item.source_name || 'RSS';
  return getPlatformDisplayName(item.platform);
}

interface FeedListProps {
  items: FeedItem[];
  loading: boolean;
  error?: string;
  showFilteredBadge?: boolean;
  emptyMessage?: string;
  onExpand: (item: FeedItem) => void;
  approveLabel?: string;
  reviewActions?: {
    onApprove: (id: string) => void;
    onHide: (id: string) => void;
  };
}

// Renders a single thumbnail image with error-safe fallback
function MediaThumb({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className="rounded-md object-cover w-full max-h-56"
      loading="lazy"
    />
  );
}

// YouTube embed thumbnail with play overlay
function YouTubeThumb({ videoId, title }: { videoId: string; title: string }) {
  return (
    <a
      href={`https://www.youtube.com/watch?v=${videoId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="relative block rounded-md overflow-hidden group"
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
        alt={title}
        className="w-full max-h-48 object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
        <div className="bg-red-600 rounded-full p-2.5">
          <Play className="h-5 w-5 text-white fill-white" />
        </div>
      </div>
    </a>
  );
}

const FeedList: React.FC<FeedListProps> = ({
  items,
  loading,
  error,
  showFilteredBadge,
  emptyMessage,
  onExpand,
  approveLabel = 'Move to feed',
  reviewActions,
}) => {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-4">
            <div className="animate-pulse space-y-2">
              <div className="h-3 bg-muted rounded w-1/3" />
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </div>
          </Card>
        ))}
      </div>
    );
  }
  if (error) {
    return <Card className="p-6 text-sm text-muted-foreground">Failed to load feed: {error}</Card>;
  }
  if (items.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        {emptyMessage ?? 'Nothing here yet.'}
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const media = getMediaFromItem(item);
        return (
          <Card
            key={item.id}
            className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${
              item.is_pinned ? 'ring-1 ring-primary/15' : ''
            } ${item.is_demoted ? 'opacity-60' : ''}`}
            onClick={() => onExpand(item)}
          >
            <div className="space-y-2.5">
              {/* Meta row */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span className="font-medium text-foreground/70">{platformLabel(item)}</span>
                {item.author && <span>· {formatAuthor(item.platform, item.author)}</span>}
                {item.published_at && (
                  <span>· {formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}</span>
                )}
                {item.is_pinned && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5 gap-1">
                    <Pin className="h-3 w-3" />
                    Pinned
                  </Badge>
                )}
                {showFilteredBadge && item.is_filtered && (
                  <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                    Hidden by fence
                  </Badge>
                )}
                {item.is_pending_review && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                    Pending review
                  </Badge>
                )}
                {typeof item.toxicity_score === 'number' && item.toxicity_score > 0 && (
                  <span className="text-[10px] text-muted-foreground" title={`Toxicity ${item.toxicity_score.toFixed(1)}/10`}>
                    · tox {item.toxicity_score.toFixed(1)}
                  </span>
                )}
                {item.rule_tags?.length > 0 && (
                  <span className="flex flex-wrap gap-1">
                    {item.rule_tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-[10px] py-0 px-1.5">{tag}</Badge>
                    ))}
                  </span>
                )}
              </div>

              {/* Title */}
              {item.title && <h3 className="font-medium leading-snug">{item.title}</h3>}

              {/* Body preview */}
              {item.body && (
                <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                  {item.body}
                </p>
              )}

              {/* Media */}
              {media.videoId ? (
                <YouTubeThumb videoId={media.videoId} title={item.title ?? 'Video'} />
              ) : media.images.length > 0 ? (
                <div className={`grid gap-1 ${media.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {media.images.slice(0, 4).map((src, i) => (
                    <MediaThumb key={i} src={src} alt={item.title ?? `Image ${i + 1}`} />
                  ))}
                </div>
              ) : null}

              {/* Footer: link + review actions */}
              <div className="flex items-center justify-between flex-wrap gap-2 pt-0.5">
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View original
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span />
                )}
                {reviewActions && (
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={() => reviewActions.onApprove(item.id)}>
                      <Check className="h-3 w-3 mr-1" />
                      {approveLabel}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => reviewActions.onHide(item.id)}>
                      <EyeOff className="h-3 w-3 mr-1" />
                      Hide
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

// ── Reply Draft Panel ────────────────────────────────────────────────────────

function ReplyDraftPanel({
  item,
  onCompose,
}: {
  item: FeedItem;
  onCompose: (draft: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<number | null>(null);

  const fetchDrafts = async () => {
    setLoading(true);
    setDrafts([]);
    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          action: 'reply_draft',
          title: item.title,
          body: item.body,
          platform: item.platform,
        },
      });
      if (error) throw error;
      if (Array.isArray(data?.drafts)) setDrafts(data.drafts as string[]);
    } catch {
      setDrafts(['Could not generate drafts — please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const copy = (i: number) => {
    navigator.clipboard.writeText(drafts[i] ?? '');
    setCopied(i);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="border-t">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open && drafts.length === 0) fetchDrafts();
        }}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
      >
        <Sparkles className="h-3.5 w-3.5" />
        <span className="font-medium">Draft a reply with AI</span>
        <span className="ml-auto">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          {loading && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          )}
          {!loading && drafts.map((draft, i) => (
            <div key={i} className="flex items-start gap-2 rounded-md border bg-background p-2.5 group">
              <p className="text-xs flex-1 leading-relaxed">{draft}</p>
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => copy(i)}
                  className="text-[10px] flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-muted-foreground hover:bg-muted transition-colors"
                >
                  {copied === i ? <Check className="h-2.5 w-2.5" /> : <Copy className="h-2.5 w-2.5" />}
                  {copied === i ? 'Copied' : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={() => onCompose(draft)}
                  className="text-[10px] flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-muted-foreground hover:bg-muted transition-colors"
                >
                  <PenSquare className="h-2.5 w-2.5" />
                  Compose
                </button>
              </div>
            </div>
          ))}
          {!loading && drafts.length > 0 && (
            <button
              type="button"
              onClick={fetchDrafts}
              className="text-[11px] flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" /> Regenerate
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function greetingForHour(hour: number): string {
  if (hour < 5) return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function deriveFirstName(
  fullName: string | null,
  email: string | null,
  metadataName: string | null,
): string {
  const fromProfile = fullName?.trim();
  if (fromProfile && /^[A-Za-zÀ-ÿ]+([ '-][A-Za-zÀ-ÿ]+)+$/.test(fromProfile)) {
    return fromProfile.split(/\s+/)[0];
  }
  if (metadataName) {
    const first = metadataName.trim().split(/\s+/)[0];
    if (first) return first;
  }
  if (email) {
    const localPart = email.split('@')[0];
    return localPart.charAt(0).toUpperCase() + localPart.slice(1);
  }
  return 'there';
}

export function TopNavFeeds() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const markedSeenRef = useRef(false);
  const [expandedItem, setExpandedItem] = useState<FeedItem | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    if (t === 'by-platform') return 'by-platform';
    if (t === 'by-rule') return 'by-rule';
    if (t === 'following') return 'following';
    return 'curated';
  });
  // Platform key to scroll to after by-platform tab renders
  const [scrollToPlatform, setScrollToPlatform] = useState<string | null>(
    () => searchParams.get('platform'),
  );
  // Rule tag to scroll to after by-rule tab renders
  const [scrollToRule, setScrollToRule] = useState<string | null>(
    () => searchParams.get('rule'),
  );
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem('feed_banner_dismissed') === '1',
  );
  const columnRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const ruleColumnRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll to the target platform column once the by-platform tab is active
  // and the items have loaded (columnRefs populated)
  useEffect(() => {
    if (activeTab !== 'by-platform' || !scrollToPlatform) return;
    // Try immediately, then retry once after a short paint delay
    const tryScroll = () => {
      const el = columnRefs.current.get(scrollToPlatform);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        // Pulse the column header briefly to visually confirm
        el.style.transition = 'box-shadow 0.3s ease';
        el.style.boxShadow = '0 0 0 2px #1A2E4A';
        setTimeout(() => { el.style.boxShadow = ''; }, 1200);
        setScrollToPlatform(null);
        // Clean up URL params without pushing a new history entry
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('tab');
          next.delete('platform');
          return next;
        }, { replace: true });
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      const t = setTimeout(tryScroll, 150);
      return () => clearTimeout(t);
    }
  }, [activeTab, scrollToPlatform, setSearchParams]);

  // Scroll to the target rule column once the by-rule tab is active
  useEffect(() => {
    if (activeTab !== 'by-rule' || !scrollToRule) return;
    const tryScroll = () => {
      const el = ruleColumnRefs.current.get(scrollToRule);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        el.style.transition = 'box-shadow 0.3s ease';
        el.style.boxShadow = '0 0 0 2px #1A2E4A';
        setTimeout(() => { el.style.boxShadow = ''; }, 1200);
        setScrollToRule(null);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('tab');
          next.delete('rule');
          return next;
        }, { replace: true });
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      const t = setTimeout(tryScroll, 150);
      return () => clearTimeout(t);
    }
  }, [activeTab, scrollToRule, setSearchParams]);

  useLogSurfaceVisit('feeds');

  const profileQuery = useQuery({
    queryKey: ['profile-feeds-meta', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('parent_lock, last_seen_at, full_name')
        .eq('id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? { parent_lock: false, last_seen_at: null, full_name: null }) as {
        parent_lock: boolean;
        last_seen_at: string | null;
        full_name: string | null;
      };
    },
    staleTime: STALE_MS,
  });

  const itemsQuery = useQuery<FeedItem[]>({
    queryKey: ['feeds-items', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_feed_items')
        .select(
          'id, platform, author, title, body, url, published_at, fetched_at, rule_tags, is_pinned, is_demoted, is_filtered, is_pending_review, toxicity_score, source_name, content_json',
        )
        .eq('user_id', user!.id)
        .order('is_pinned', { ascending: false })
        .order('is_demoted', { ascending: true })
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('fetched_at', { ascending: false })
        .limit(FEED_LIMIT);
      if (error) throw error;
      return (data ?? []) as FeedItem[];
    },
    staleTime: STALE_MS,
  });

  const items = itemsQuery.data ?? [];
  const lastSeen = profileQuery.data?.last_seen_at ?? null;
  const parentLock = profileQuery.data?.parent_lock ?? false;
  const fullName = profileQuery.data?.full_name ?? null;

  const firstName = deriveFirstName(
    fullName,
    user?.email ?? null,
    (user?.user_metadata?.full_name as string | undefined) ?? null,
  );
  const greeting = greetingForHour(new Date().getHours());

  const curated = useMemo(() => {
    return items
      .filter((i) => !i.is_filtered && !i.is_pending_review)
      .sort((a, b) => {
        // Pinned items always first
        if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
        // Demoted items always last
        if (a.is_demoted !== b.is_demoted) return a.is_demoted ? 1 : -1;
        // Sort by published_at DESC (when the post was written), fallback to fetched_at
        const aTime = a.published_at ?? a.fetched_at;
        const bTime = b.published_at ?? b.fetched_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
  }, [items]);
  const reviewQueue = useMemo(
    () => items.filter((i) => i.is_pending_review && !i.is_filtered),
    [items],
  );

  // Separate query for hard-fenced posts — these are excluded from the main
  // 100-item window so we fetch them independently.
  const fencedQuery = useQuery<FeedItem[]>({
    queryKey: ['feeds-fenced', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_feed_items')
        .select(
          'id, platform, author, title, body, url, published_at, fetched_at, rule_tags, is_pinned, is_demoted, is_filtered, is_pending_review, toxicity_score, source_name, content_json',
        )
        .eq('user_id', user!.id)
        .eq('is_filtered', true)
        .order('fetched_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as FeedItem[];
    },
    staleTime: STALE_MS,
  });
  const fencedItems = fencedQuery.data ?? [];

  // ── Following feed ────────────────────────────────────────────────────────
  // Only fetch when the tab is active to avoid unnecessary DB calls.
  const followingQuery = useQuery<FollowingFeedItem[]>({
    queryKey: ['feeds-following', user?.id],
    enabled: Boolean(user?.id) && activeTab === 'following',
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_following_feed', {
        p_user_id: user!.id,
      });
      if (error) throw error;
      return (data ?? []) as FollowingFeedItem[];
    },
    staleTime: STALE_MS,
  });
  const followingItems = followingQuery.data ?? [];

  // Group following items by curator for TweetDeck columns
  const followingByCurator = useMemo(() => {
    const groups = new Map<string, { items: FollowingFeedItem[]; username: string | null; fullName: string | null; avatarUrl: string | null }>();
    for (const item of followingItems) {
      const key = item.curator_id;
      if (!groups.has(key)) {
        groups.set(key, {
          items: [],
          username: item.curator_username,
          fullName: item.curator_full_name,
          avatarUrl: item.curator_avatar_url,
        });
      }
      groups.get(key)!.items.push(item);
    }
    return Array.from(groups.entries()).sort(([, a], [, b]) =>
      (a.fullName ?? a.username ?? '').localeCompare(b.fullName ?? b.username ?? ''),
    );
  }, [followingItems]);

  const itemsByPlatform = useMemo(() => {
    const groups = new Map<string, FeedItem[]>();
    for (const i of curated) {
      const key = i.platform === 'rss' ? `rss:${i.source_name ?? 'RSS'}` : i.platform;
      const arr = groups.get(key) ?? [];
      arr.push(i);
      groups.set(key, arr);
    }
    // Sort each column newest-fetched-first
    for (const arr of groups.values()) {
      arr.sort((a, b) => new Date(b.fetched_at).getTime() - new Date(a.fetched_at).getTime());
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [curated]);

  // Group curated items by rule_tag for the "By Rule" column view
  const itemsByRule = useMemo(() => {
    const groups = new Map<string, FeedItem[]>();
    for (const i of curated) {
      const tags: string[] = Array.isArray(i.rule_tags) ? i.rule_tags : [];
      if (tags.length === 0) {
        const arr = groups.get('__untagged__') ?? [];
        arr.push(i);
        groups.set('__untagged__', arr);
      } else {
        for (const tag of tags) {
          const arr = groups.get(tag) ?? [];
          arr.push(i);
          groups.set(tag, arr);
        }
      }
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => new Date(b.fetched_at).getTime() - new Date(a.fetched_at).getTime());
    }
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === '__untagged__') return 1;
      if (b === '__untagged__') return -1;
      return a.localeCompare(b);
    });
  }, [curated]);

  // New items since last visit, grouped by source key
  const newSinceLastSeen = useMemo(() => {
    if (!lastSeen) return new Map<string, number>();
    const cutoff = new Date(lastSeen).getTime();
    const counts = new Map<string, number>();
    for (const item of curated) {
      if (new Date(item.fetched_at).getTime() > cutoff) {
        const key = item.platform === 'rss' ? `rss:${item.source_name ?? 'RSS'}` : item.platform;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return counts;
  }, [curated, lastSeen]);

  const totalNew = useMemo(
    () => Array.from(newSinceLastSeen.values()).reduce((a, b) => a + b, 0),
    [newSinceLastSeen],
  );

  const scrollToColumn = (key: string) => {
    setActiveTab('by-platform');
    setTimeout(() => {
      const el = columnRefs.current.get(key);
      if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 80);
  };

  const dismissBanner = () => {
    setBannerDismissed(true);
    sessionStorage.setItem('feed_banner_dismissed', '1');
  };

  const approveFromQueue = async (id: string) => {
    const { error } = await supabase
      .from('social_feed_items')
      .update({ is_pending_review: false })
      .eq('id', id);
    if (error) {
      toast.error(`Failed: ${error.message}`);
      return;
    }
    toast.success('Moved to your feed.');
    queryClient.invalidateQueries({ queryKey: ['feeds-items', user?.id] });
  };

  const hideFromQueue = async (id: string) => {
    const { error } = await supabase
      .from('social_feed_items')
      .update({ is_filtered: true, is_pending_review: false })
      .eq('id', id);
    if (error) {
      toast.error(`Failed: ${error.message}`);
      return;
    }
    toast.success('Hidden.');
    queryClient.invalidateQueries({ queryKey: ['feeds-items', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['feeds-fenced', user?.id] });
  };

  // Restore a hard-fenced post — clears is_filtered and resets rules_applied_at
  // so the rule engine re-evaluates it on the next cycle.
  const restorePost = async (id: string) => {
    const { error } = await supabase
      .from('social_feed_items')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ is_filtered: false, is_pending_review: false, rules_applied_at: null } as any)
      .eq('id', id);
    if (error) {
      toast.error(`Failed: ${error.message}`);
      return;
    }
    toast.success('Restored to your feed.');
    queryClient.invalidateQueries({ queryKey: ['feeds-items', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['feeds-fenced', user?.id] });
  };

  useEffect(() => {
    if (markedSeenRef.current) return;
    if (profileQuery.isLoading || itemsQuery.isLoading) return;
    if (!user?.id) return;
    markedSeenRef.current = true;
    supabase.rpc('mark_feed_seen').then(({ error }) => {
      if (error) console.warn('mark_feed_seen failed:', error.message);
      queryClient.invalidateQueries({ queryKey: ['profile-feeds-meta', user.id] });
    });
  }, [user?.id, profileQuery.isLoading, itemsQuery.isLoading, queryClient]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['feeds-items', user?.id] });
  };

  const [reviewUnlocked, setReviewUnlocked] = useState(() => isUnlockedThisSession(user?.id));

  return (
    <div className="py-8 px-6 space-y-5">

      {/* ── Header row ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {greeting}{firstName !== 'there' ? `, ${firstName}` : ''}.
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Your latest content, background-synced from all connected sources.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refresh} variant="outline" size="sm" disabled={itemsQuery.isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${itemsQuery.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            className="bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
            onClick={() => setComposeOpen(true)}
          >
            <PenSquare className="h-4 w-4 mr-2" />
            Compose
          </Button>
        </div>
      </div>

      {/* ── Since-last-visit banner ───────────────────────────────────────── */}
      {!bannerDismissed && !itemsQuery.isLoading && !profileQuery.isLoading && totalNew > 0 && (
        <div className="rounded-lg border border-[#1A2E4A]/15 bg-[#1A2E4A]/[0.03] px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 flex-1">
              <p className="text-sm">
                <strong>{totalNew} new {totalNew === 1 ? 'item' : 'items'}</strong> since your last visit —{' '}
                <span className="text-muted-foreground">click a source to jump to its column.</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(newSinceLastSeen.entries())
                  .sort(([, a], [, b]) => b - a)
                  .map(([key, count]) => (
                    <button
                      key={key}
                      onClick={() => scrollToColumn(key)}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-[#1A2E4A]/20 bg-background hover:bg-[#1A2E4A]/5 px-2.5 py-1 transition-colors"
                    >
                      <span className="font-medium">
                        {key.startsWith('rss:') ? key.slice(4) : getPlatformDisplayName(key)}
                      </span>
                      <Badge variant="secondary" className="text-[10px] py-0 px-1 h-4">
                        {count}
                      </Badge>
                    </button>
                  ))}
              </div>
            </div>
            <button
              onClick={dismissBanner}
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5"
              aria-label="Dismiss"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <ComposeDialog open={composeOpen} onOpenChange={setComposeOpen} />

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        <TabsList>
          <TabsTrigger value="curated">Curated</TabsTrigger>
          <TabsTrigger value="by-platform">By platform</TabsTrigger>
          <TabsTrigger value="by-rule">By rule</TabsTrigger>
          <TabsTrigger value="following">
            Following
            {followingItems.length > 0 && (
              <Badge variant="secondary" className="ml-2 text-[10px] py-0 px-1.5">
                {followingByCurator.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="review">
            Review Queue
            {fencedItems.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-[10px] py-0 px-1.5" title="Hidden by fence">
                {fencedItems.length} fenced
              </Badge>
            )}
            {reviewQueue.length > 0 && (
              <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1.5 border-amber-400 text-amber-700 bg-amber-50" title="Pending your review">
                {reviewQueue.length} pending
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Curated ── */}
        <TabsContent value="curated">
          <div className="max-w-3xl">
            <FeedList
              items={curated}
              loading={itemsQuery.isLoading}
              error={itemsQuery.isError ? (itemsQuery.error as Error).message : undefined}
              emptyMessage="Nothing here yet. Connect a platform or add an RSS source to a rule."
              onExpand={setExpandedItem}
            />
          </div>
        </TabsContent>

        {/* ── By platform — full-width TweetDeck columns ── */}
        <TabsContent value="by-platform">
          {itemsQuery.isLoading ? (
            <div className="max-w-3xl">
              <FeedList items={[]} loading={true} onExpand={setExpandedItem} />
            </div>
          ) : itemsByPlatform.length === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground max-w-3xl">
              No platform-grouped content yet.
            </Card>
          ) : (
            <div className="-mx-6 px-6 overflow-x-auto">
              <div
                className="flex gap-4 pb-6"
                style={{ minWidth: `${itemsByPlatform.length * 340}px` }}
              >
                {itemsByPlatform.map(([key, group]) => {
                  const newCount = newSinceLastSeen.get(key) ?? 0;
                  return (
                    <div
                      key={key}
                      ref={(el) => {
                        if (el) columnRefs.current.set(key, el);
                        else columnRefs.current.delete(key);
                      }}
                      className="flex flex-col flex-shrink-0 rounded-xl border bg-muted/20 shadow-sm"
                      style={{ width: 'clamp(300px, calc((100vw - 3rem) / ' + itemsByPlatform.length + '), 420px)' }}
                    >
                      {/* Column header */}
                      <div className="flex items-center justify-between px-3 py-2.5 border-b bg-background rounded-t-xl sticky top-0 z-10">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {key.startsWith('rss:') ? key.slice(4) : getPlatformDisplayName(key)}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {newCount > 0 && (
                            <Badge className="text-[10px] py-0 px-1.5 bg-[#1A2E4A] text-white hover:bg-[#1A2E4A]/90">
                              {newCount} new
                            </Badge>
                          )}
                          <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                            {group.length}
                          </Badge>
                        </div>
                      </div>

                      {/* Scrollable column body */}
                      <div className="flex-1 overflow-y-auto max-h-[72vh] p-2 space-y-2">
                        {group.map((item) => {
                          const media = getMediaFromItem(item);
                          const isNew = lastSeen
                            ? new Date(item.fetched_at).getTime() > new Date(lastSeen).getTime()
                            : false;
                          return (
                            <div
                              key={item.id}
                              onClick={() => setExpandedItem(item)}
                              className={`rounded-lg border bg-background p-3 cursor-pointer hover:shadow-sm transition-shadow space-y-2 ${
                                isNew ? 'border-l-2 border-l-[#1A2E4A]/40' : ''
                              }`}
                            >
                              {/* Meta */}
                              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                {item.author && (
                                  <span className="font-medium truncate max-w-[140px]">
                                    {formatAuthor(item.platform, item.author)}
                                  </span>
                                )}
                                {item.published_at && (
                                  <span className="shrink-0">
                                    · {formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}
                                  </span>
                                )}
                              </div>

                              {/* Title */}
                              {item.title && (
                                <p className="text-xs font-medium leading-snug line-clamp-2">
                                  {item.title}
                                </p>
                              )}

                              {/* Body (only if no title) */}
                              {item.body && !item.title && (
                                <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                                  {item.body}
                                </p>
                              )}

                              {/* Thumbnail */}
                              {media.videoId ? (
                                <div className="relative rounded-md overflow-hidden">
                                  <img
                                    src={`https://i.ytimg.com/vi/${media.videoId}/mqdefault.jpg`}
                                    alt={item.title ?? 'Video'}
                                    className="w-full object-cover max-h-28"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <div className="bg-red-600 rounded-full p-1.5">
                                      <Play className="h-3 w-3 text-white fill-white" />
                                    </div>
                                  </div>
                                </div>
                              ) : media.images.length > 0 ? (
                                <MediaThumb src={media.images[0]} alt={item.title ?? 'Image'} />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── By rule — columns grouped by rule_tag ── */}
        <TabsContent value="by-rule">
          {itemsQuery.isLoading ? (
            <div className="max-w-3xl">
              <FeedList items={[]} loading={true} onExpand={setExpandedItem} />
            </div>
          ) : itemsByRule.length === 0 ? (
            <Card className="p-6 text-sm text-muted-foreground max-w-3xl">
              <p className="font-medium mb-1">No rule-tagged content yet.</p>
              <p>Once your listening rules and fences run, posts will appear here grouped by which rule matched them.</p>
              <button
                type="button"
                onClick={() => navigate('/dashboard/rules')}
                className="mt-3 text-xs text-[#1A2E4A] underline underline-offset-2 hover:opacity-70"
              >
                Go to Rules →
              </button>
            </Card>
          ) : (
            <div className="-mx-6 px-6 overflow-x-auto">
              <div
                className="flex gap-4 pb-6"
                style={{ minWidth: `${itemsByRule.length * 340}px` }}
              >
                {itemsByRule.map(([tag, group]) => {
                  const isUntagged = tag === '__untagged__';
                  const label = isUntagged ? 'No rule matched' : tag;
                  return (
                    <div
                      key={tag}
                      ref={(el) => {
                        if (el) ruleColumnRefs.current.set(tag, el);
                        else ruleColumnRefs.current.delete(tag);
                      }}
                      className="flex flex-col flex-shrink-0 rounded-xl border bg-muted/20 shadow-sm"
                      style={{ width: 'clamp(300px, calc((100vw - 3rem) / ' + Math.min(itemsByRule.length, 4) + '), 420px)' }}
                    >
                      {/* Column header */}
                      <div className="flex items-center justify-between px-3 py-2.5 border-b bg-background rounded-t-xl sticky top-0 z-10">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
                            {label}
                          </span>
                          {!isUntagged && (
                            <button
                              type="button"
                              title="View this rule"
                              onClick={() => navigate(`/dashboard/rules?highlight=${encodeURIComponent(tag)}`)}
                              className="text-[10px] text-[#1A2E4A] underline underline-offset-2 hover:opacity-60 shrink-0"
                            >
                              view rule
                            </button>
                          )}
                        </div>
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5 shrink-0">
                          {group.length}
                        </Badge>
                      </div>

                      {/* Scrollable column body */}
                      <div className="flex-1 overflow-y-auto max-h-[72vh] p-2 space-y-2">
                        {group.map((item) => {
                          const media = getMediaFromItem(item);
                          const isNew = lastSeen
                            ? new Date(item.fetched_at).getTime() > new Date(lastSeen).getTime()
                            : false;
                          return (
                            <div
                              key={item.id}
                              onClick={() => setExpandedItem(item)}
                              className={`rounded-lg border bg-background p-3 cursor-pointer hover:shadow-sm transition-shadow space-y-2 ${
                                isNew ? 'border-l-2 border-l-[#1A2E4A]/40' : ''
                              }`}
                            >
                              {/* Platform badge */}
                              <div className="flex items-center justify-between gap-1">
                                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                  {item.platform === 'rss' ? (item.source_name ?? 'RSS') : getPlatformDisplayName(item.platform)}
                                </span>
                                {item.published_at && (
                                  <span className="text-[10px] text-muted-foreground shrink-0">
                                    {formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}
                                  </span>
                                )}
                              </div>

                              {/* Author */}
                              {item.author && (
                                <div className="text-[11px] text-muted-foreground font-medium truncate">
                                  {formatAuthor(item.platform, item.author)}
                                </div>
                              )}

                              {/* Title */}
                              {item.title && (
                                <p className="text-xs font-medium leading-snug line-clamp-2">
                                  {item.title}
                                </p>
                              )}

                              {/* Body preview */}
                              {!item.title && item.body && (
                                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                                  {item.body}
                                </p>
                              )}

                              {/* Thumbnail */}
                              {media.images[0] && (
                                <img
                                  src={media.images[0]}
                                  alt=""
                                  className="w-full h-24 object-cover rounded-md"
                                  loading="lazy"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              )}

                              {/* Other rule tags on this item */}
                              {item.rule_tags?.filter(t => t !== tag).length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {item.rule_tags.filter(t => t !== tag).map(t => (
                                    <Badge key={t} variant="secondary" className="text-[10px] py-0 px-1.5">
                                      {t}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Following — one column per followed curator ── */}
        <TabsContent value="following">
          {followingQuery.isLoading ? (
            <div className="max-w-3xl">
              <FeedList items={[]} loading={true} onExpand={setExpandedItem} />
            </div>
          ) : followingByCurator.length === 0 ? (
            <Card className="p-8 text-sm text-muted-foreground max-w-2xl">
              <p className="font-medium text-base text-foreground mb-2">Nobody to follow yet.</p>
              <p className="leading-relaxed mb-4">
                When you follow a curator their public feed appears here as a column — one per
                person, TweetDeck-style. Start with the founding curators or discover someone new.
              </p>
              <div className="flex gap-3 flex-wrap">
                <Link to="/dashboard/explore">
                  <button
                    type="button"
                    className="text-xs text-[#1A2E4A] underline underline-offset-2 hover:opacity-70"
                  >
                    Browse Explore →
                  </button>
                </Link>
              </div>
            </Card>
          ) : (
            <div className="-mx-6 px-6 overflow-x-auto">
              <div
                className="flex gap-4 pb-6"
                style={{ minWidth: `${followingByCurator.length * 340}px` }}
              >
                {followingByCurator.map(([curatorId, curator]) => {
                  const displayName = curator.fullName ?? (curator.username ? `@${curator.username}` : 'Unknown');
                  const profileUrl = curator.username ? `/u/${curator.username}` : null;
                  return (
                    <div
                      key={curatorId}
                      className="flex flex-col flex-shrink-0 rounded-xl border bg-muted/20 shadow-sm"
                      style={{ width: `clamp(300px, calc((100vw - 3rem) / ${Math.min(followingByCurator.length, 4)}), 420px)` }}
                    >
                      {/* Column header */}
                      <div className="flex items-center justify-between px-3 py-2.5 border-b bg-background rounded-t-xl sticky top-0 z-10">
                        <div className="flex items-center gap-2 min-w-0">
                          {curator.avatarUrl ? (
                            <img
                              src={curator.avatarUrl}
                              alt={displayName}
                              className="w-5 h-5 rounded-full object-cover shrink-0"
                            />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[9px] font-semibold shrink-0">
                              {displayName[0]?.toUpperCase()}
                            </div>
                          )}
                          {profileUrl ? (
                            <Link
                              to={profileUrl}
                              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground truncate transition-colors"
                            >
                              {displayName}
                            </Link>
                          ) : (
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
                              {displayName}
                            </span>
                          )}
                        </div>
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5 shrink-0">
                          {curator.items.length}
                        </Badge>
                      </div>

                      {/* Scrollable column body */}
                      <div className="flex-1 overflow-y-auto max-h-[72vh] p-2 space-y-2">
                        {curator.items.map((item) => {
                          const media = getMediaFromItem(item as unknown as FeedItem);
                          return (
                            <div
                              key={item.id}
                              onClick={() => setExpandedItem(item as unknown as FeedItem)}
                              className="rounded-lg border bg-background p-3 cursor-pointer hover:shadow-sm transition-shadow space-y-2"
                            >
                              {/* Platform + time */}
                              <div className="flex items-center justify-between gap-1">
                                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                  {item.platform === 'rss' ? (item.source_name ?? 'RSS') : getPlatformDisplayName(item.platform)}
                                </span>
                                {item.published_at && (
                                  <span className="text-[10px] text-muted-foreground shrink-0">
                                    {formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}
                                  </span>
                                )}
                              </div>

                              {/* Author */}
                              {item.author && (
                                <div className="text-[11px] text-muted-foreground font-medium truncate">
                                  {item.author}
                                </div>
                              )}

                              {/* Title */}
                              {item.title && (
                                <p className="text-xs font-medium leading-snug line-clamp-2">
                                  {item.title}
                                </p>
                              )}

                              {/* Body fallback */}
                              {item.body && !item.title && (
                                <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                                  {item.body}
                                </p>
                              )}

                              {/* Thumbnail */}
                              {media.videoId ? (
                                <div className="relative rounded-md overflow-hidden">
                                  <img
                                    src={`https://i.ytimg.com/vi/${media.videoId}/mqdefault.jpg`}
                                    alt={item.title ?? 'Video'}
                                    className="w-full object-cover max-h-28"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <div className="bg-red-600 rounded-full p-1.5">
                                      <Play className="h-3 w-3 text-white fill-white" />
                                    </div>
                                  </div>
                                </div>
                              ) : media.images.length > 0 ? (
                                <MediaThumb src={media.images[0]} alt={item.title ?? 'Image'} />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Review queue ── */}
        <TabsContent value="review">
          <div className="max-w-3xl">
            {parentLock && !reviewUnlocked ? (
              <PinGate userId={user?.id} onUnlocked={() => setReviewUnlocked(true)} />
            ) : (
              <div className="space-y-8">
                <section>
                  <div className="mb-3">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                      Hidden by fence
                      {fencedItems.length > 0 && (
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                          {fencedItems.length}
                        </Badge>
                      )}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      These posts matched a fence rule and were blocked. Restore any you want back
                      in your feed, or leave them hidden.
                    </p>
                  </div>
                  <FeedList
                    items={fencedItems}
                    loading={fencedQuery.isLoading}
                    error={fencedQuery.isError ? (fencedQuery.error as Error).message : undefined}
                    emptyMessage="No posts are currently hidden by your fences."
                    onExpand={setExpandedItem}
                    reviewActions={{ onApprove: restorePost, onHide: hideFromQueue }}
                    approveLabel="Restore to feed"
                  />
                </section>

                <section>
                  <div className="mb-3">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                      Pending your review
                      {reviewQueue.length > 0 && (
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                          {reviewQueue.length}
                        </Badge>
                      )}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Soft-fenced by heuristics (new accounts, low followers). Move to your feed or
                      dismiss.
                    </p>
                  </div>
                  <FeedList
                    items={reviewQueue}
                    loading={itemsQuery.isLoading}
                    error={itemsQuery.isError ? (itemsQuery.error as Error).message : undefined}
                    emptyMessage="Nothing pending. Posts caught by soft fences land here."
                    onExpand={setExpandedItem}
                    reviewActions={{ onApprove: approveFromQueue, onHide: hideFromQueue }}
                  />
                </section>
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* ── Expanded post sheet ─────────────────────────────────────────── */}
      <Sheet open={Boolean(expandedItem)} onOpenChange={(o) => { if (!o) setExpandedItem(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
          {expandedItem && (() => {
            const item = expandedItem;
            const media = getMediaFromItem(item);
            const actions = getEngagementActions(item);
            return (
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-background z-10">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold">{platformLabel(item)}</span>
                    {item.author && (
                      <span className="text-muted-foreground">· {formatAuthor(item.platform, item.author)}</span>
                    )}
                    {item.published_at && (
                      <span className="text-muted-foreground">
                        · {formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setExpandedItem(null)}
                    className="rounded-full p-1 hover:bg-muted transition-colors"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                  {item.title && (
                    <h2 className="text-lg font-semibold leading-snug">{item.title}</h2>
                  )}
                  {item.body && (
                    <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">
                      {item.body}
                    </p>
                  )}

                  {/* Media — full size */}
                  {media.videoId ? (
                    <div className="rounded-lg overflow-hidden">
                      <iframe
                        src={`https://www.youtube.com/embed/${media.videoId}`}
                        title={item.title ?? 'Video'}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full aspect-video"
                      />
                    </div>
                  ) : media.images.length > 0 ? (
                    <div className={`grid gap-2 ${media.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {media.images.map((src, i) => (
                        <a key={i} href={src} target="_blank" rel="noopener noreferrer">
                          <MediaThumb src={src} alt={item.title ?? `Image ${i + 1}`} />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>

                {/* Engagement actions */}
                {actions.length > 0 && (
                  <div className="border-t p-4 space-y-2 bg-muted/30">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                      Engage
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {actions.map((action) => {
                        // In-app actions — stay inside SocialScoop
                        if (action.inApp === 'reply') {
                          return (
                            <button
                              key={action.label}
                              type="button"
                              onClick={() => {
                                const author = item.author
                                  ? formatAuthor(item.platform, item.author)
                                  : null;
                                const context = author
                                  ? `Replying to ${author}:\n\n`
                                  : 'Replying to:\n\n';
                                // eslint-disable-next-line no-empty
                                try { sessionStorage.setItem('compose_prefill', context); } catch {}
                                setExpandedItem(null);
                                setTimeout(() => setComposeOpen(true), 120);
                              }}
                              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border bg-background hover:bg-muted transition-colors"
                            >
                              {action.icon}
                              {action.label}
                            </button>
                          );
                        }
                        if (action.inApp === 'repost') {
                          const repostText = [
                            item.author ? `${formatAuthor(item.platform, item.author)}:` : null,
                            item.title ?? item.body?.slice(0, 200),
                            item.url,
                          ].filter(Boolean).join('\n\n');
                          return (
                            <button
                              key={action.label}
                              type="button"
                              onClick={() => {
                                // eslint-disable-next-line no-empty
                                try { sessionStorage.setItem('compose_prefill', repostText); } catch {}
                                setExpandedItem(null);
                                setTimeout(() => setComposeOpen(true), 120);
                              }}
                              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border bg-background hover:bg-muted transition-colors"
                            >
                              {action.icon}
                              {action.label}
                            </button>
                          );
                        }
                        if (action.inApp === 'share') {
                          return (
                            <button
                              key={action.label}
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(item.url ?? '').then(() => {
                                  toast.success('Link copied to clipboard');
                                }).catch(() => {
                                  toast.error('Could not copy link');
                                });
                              }}
                              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border bg-background hover:bg-muted transition-colors"
                            >
                              {action.icon}
                              {action.label}
                            </button>
                          );
                        }
                        // 'Open original' — explicit external link, user intent is clear
                        return action.href ? (
                          <a
                            key={action.label}
                            href={action.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border bg-background hover:bg-muted transition-colors opacity-60 hover:opacity-100"
                          >
                            {action.icon}
                            {action.label}
                          </a>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* AI reply drafts */}
                <ReplyDraftPanel item={item} onCompose={(draft) => {
                  setExpandedItem(null);
                  // Small delay so the sheet closes first
                  setTimeout(() => {
                    setComposeOpen(true);
                    sessionStorage.setItem('compose_prefill', draft);
                  }, 200);
                }} />
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default TopNavFeeds;
